import React, { Children, createContext, cloneElement, isValidElement, useContext, useState, useEffect } from 'react';
import styles from './toggle-radio.css';

type RadioContextModel = {
  name: string;
  value: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

type ToggleRadioGroupProps = React.PropsWithChildren<RadioContextModel>;

const RadioContext = createContext<RadioContextModel>({ name: '', value: '', handleChange: () => { } });
const RadioProvider = RadioContext.Provider;

const ToggleRadioGroup = ({
  name,
  value,
  handleChange,
  children,
}: ToggleRadioGroupProps) => {
  const columns = {
    gridTemplateColumns: `repeat(${Children.count(children)}, 1fr)`,
  };

  const [selectedOrder, setSelectedOrder] = useState(0);
  const perc = `${100 * selectedOrder}%`;

  return (
    <RadioProvider value={{
      name,
      value,
      handleChange,
    }}
    >
      <div style={columns} className={styles.buttonGroupContainer}>
        {Children.map(children, (child, i) => isValidElement(child) && (
          <div
            className={styles.buttonContainer}
            key={`${i};${child.props.value}`}
          >
            {i === 0 && <div className={styles.first} style={{ transform: `translate(${perc})` }} />}
            {cloneElement<any>(child, { orderNbr: i, setOrder: (val: number) => setSelectedOrder(val) })}
            <div />
          </div>
        ))}
      </div>
    </RadioProvider>
  );
};

type ToggleRadioButtonProps = {
  value: string,
  text: string,
  orderNbr?: number,
  setOrder?: (order: number) => void,
};

const ToggleRadioButton = ({
  value,
  text,
  orderNbr,
  setOrder,
}: ToggleRadioButtonProps) => {
  const { value: currentValue, name, handleChange } = useContext(RadioContext);
  useEffect(() => {
    if (value === currentValue) {
      setOrder && setOrder(orderNbr || 0);
    }
  }, [value, currentValue]);
  return (
    <label
      htmlFor={value}
      className={`${styles.button} ${value === currentValue ? styles.active : ''}`}
    >
      <span>{text}</span>
      <input
        id={value}
        type="radio"
        name={name}
        checked={value === currentValue}
        value={value}
        onChange={handleChange}
      />
    </label>
  );
};

export { ToggleRadioGroup, ToggleRadioButton };
